import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import CustomInput from '../../common/CustomInput';
import NewToolTip from '../../common/New-ToolTip';
import Button from '../../common/Button';
import ConfirmationModalNew from '../../common/ConfirmationModalNew';
import PasswordHistoryErrorModal from '../../common/PasswordHistoryErrorModal';
import { ReactSVG } from 'react-svg';
import { updateUsername, changeMyProfilePassword, validateCurrentPassword, logout } from '../../../redux/modules/user/actions';

const EmailPassword = ({ user }) => {

    const [activeTab, setActiveTab] = useState(null);
    const bestPractices = 'flex items-center font-bold text-gray-800 text-body-md border rounded pl-16 py-5 my-4 bg-gray-20 border-gray-200'
    const [newEmail, setNewEmail] = useState("");
    const [confirmEmail, setConfirmEmail] = useState("");
    const [showPasswordChangedPopup, setShowPasswordChangedPopup] = useState(false);
    const [showUsernameUpdatedPopup, setShowUsernameUpdatedPopup] = useState(false);
    const [hasPasswordHistoryError, setHasPasswordHistoryError] = useState(false);
    const [lastOperation, setLastOperation] = useState(null);
    const [prevUsernameLoading, setPrevUsernameLoading] = useState(false);
    const [prevPasswordLoading, setPrevPasswordLoading] = useState(false);
    const [currentPassword, setCurrentPassword] = useState("");
    const [currentPasswordError, setCurrentPasswordError] = useState("");
    const [currentPasswordValid, setCurrentPasswordValid] = useState(null); // null = not checked, true = valid, false = invalid
    const [password, setPassword] = useState("");
    const [passwordAgain, setPasswordAgain] = useState("");
    const [passwordValidity, setPasswordValidity] = useState({
        isLongEnough: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumber: false,
        hasSymbol: false,
        noPersonalInfo: true,
    });

    const renderValidationDot = (isValid, additionalClasses = '') => {
        return (
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${additionalClasses} ${isValid ? 'bg-green-600 text-white' : 'bg-gray-400'
                }`}>
                {isValid ? '✓' : ''}
            </span>
        );
    };

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const updateUsernameLoading = useSelector((state) => state.users.updateUsernameLoading);
    const updateUsernameSuccess = useSelector((state) => state.users.updateUsernameSuccess);
    const changeMyProfilePasswordLoading = useSelector((state) => state.users.changeMyProfilePasswordLoading);
    const changeMyProfilePasswordSuccess = useSelector((state) => state.users.changeMyProfilePasswordSuccess);
    const changeMyProfilePasswordError = useSelector((state) => state.users.changeMyProfilePasswordError);
    const validateCurrentPasswordLoading = useSelector((state) => state.users.validateCurrentPasswordLoading);
    const currentLoggedInUserId = useSelector((state) => state.users?.userInfo?.userId || state.users?.currentLoggedInUserDetail?.userId || state.users?.userId);

    // Monitor username update success
    useEffect(() => {
        if (updateUsernameLoading) {
            setLastOperation('username');
        } else if (prevUsernameLoading && updateUsernameSuccess && lastOperation === 'username') {
            setShowUsernameUpdatedPopup(true);
            setLastOperation(null);
            dispatch({ type: "CLEAR_UPDATE_USERNAME_SUCCESS" });
        }
        setPrevUsernameLoading(updateUsernameLoading);
    }, [updateUsernameLoading, updateUsernameSuccess, prevUsernameLoading, lastOperation, dispatch]);

    // Monitor password change success and errors
    useEffect(() => {
        if (changeMyProfilePasswordLoading) {
            setLastOperation('password');
        } else if (prevPasswordLoading && lastOperation === 'password') {
            const error = changeMyProfilePasswordError;
            if (!error) {
                // Password change successful
                setHasPasswordHistoryError(false);
                setShowPasswordChangedPopup(true);
                dispatch({ type: "CLEAR_CHANGE_MYPROFILE_PASSWORD_SUCCESS" });
            } else {
                // Check if error is related to password history
                const errorMessageLower = String(error).toLowerCase();
                const isPasswordHistoryErrorCheck = ['previous', 'history', 'last five', 'previously used', 'passwordhistoryerror']
                    .some(keyword => errorMessageLower.includes(keyword));
                
                if (isPasswordHistoryErrorCheck) {
                    setHasPasswordHistoryError(true);
                    setShowPasswordChangedPopup(true);
                }
            }
            setLastOperation(null);
        }
        setPrevPasswordLoading(changeMyProfilePasswordLoading);
    }, [changeMyProfilePasswordLoading, changeMyProfilePasswordError, prevPasswordLoading, lastOperation, dispatch]);

    // Function to validate current password automatically
    const validateCurrentPasswordDebounced = useCallback(async (password) => {
        if (!password.trim()) {
            setCurrentPasswordError("");
            setCurrentPasswordValid(null);
            return;
        }

        try {
            setCurrentPasswordError("");
            // Use user's email instead of userId for validation
            const userEmail = user?.primaryEmail || user?.username;
            
            const result = await dispatch(validateCurrentPassword(userEmail, password));
            
            if (result.isValid) {
                setCurrentPasswordValid(true);
                setCurrentPasswordError("");
            } else {
                setCurrentPasswordValid(false);
                setCurrentPasswordError(result.error || "Current password is incorrect.");
            }
        } catch (error) {
            setCurrentPasswordValid(false);
            setCurrentPasswordError("Failed to validate current password.");
        }
    }, [dispatch, user]);

    // Auto-validation effect with debounce
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (currentPassword.trim()) {
                validateCurrentPasswordDebounced(currentPassword);
            }
        }, 1000); // 1 second debounce

        return () => clearTimeout(timeoutId);
    }, [currentPassword, validateCurrentPasswordDebounced]);

    const isValidEmail = (value) => /[^@\s]+@[^@\s]+\.[^@\s]+/.test(value);
    const emailsMatch = newEmail && confirmEmail && newEmail === confirmEmail;
    const isEmailDifferentFromCurrent = newEmail && user?.username && newEmail.trim() !== user.username.trim();
    const profileUserId = user?.userId || user?.id;
    const isSelf = profileUserId && currentLoggedInUserId && String(profileUserId) === String(currentLoggedInUserId);
    const canSubmitEmail = emailsMatch && isValidEmail(newEmail) && isEmailDifferentFromCurrent && isSelf && !updateUsernameLoading;

    const handleSubmitEmail = async () => {
        if (!canSubmitEmail) return;
        const userId = profileUserId;
        if (!userId || !isSelf) return;
        await dispatch(updateUsername(userId, user.username, newEmail));
    };

    const meetsPasswordRequirements = Object.values(passwordValidity).every(Boolean);
    const hasPasswordEntries = Boolean(currentPassword && password && passwordAgain);
    const passwordsMatch = Boolean(password && passwordAgain && password === passwordAgain);
    const canAttemptPasswordUpdate = hasPasswordEntries && passwordsMatch && meetsPasswordRequirements && isSelf && currentPasswordValid === true;

    const validatePassword = (pwd, currentUser) => {
        let noPersonalInfo = true;
        if (currentUser && pwd) {
            const lower = pwd.toLowerCase();
            const forbidden = [
                currentUser.firstName?.toLowerCase(),
                currentUser.lastName?.toLowerCase(),
                currentUser.primaryEmail?.split('@')[0]?.toLowerCase(),
            ].filter(Boolean);
            noPersonalInfo = !forbidden.some((w) => lower.includes(w));
        }

        return {
            isLongEnough: pwd.length >= 10,
            hasUpperCase: /[A-Z]/.test(pwd),
            hasLowerCase: /[a-z]/.test(pwd),
            hasNumber: /[0-9]/.test(pwd),
            hasSymbol: /[@#^?_!{}%]/.test(pwd),
            noPersonalInfo,
        };
    };


    const ActionButtons = ({ onCancel, onUpdate, disabled, updateLabel = "Update" }) => {
        return (
            <div className="flex items-center w-full gap-4 p-4">
                <Button
                    className="w-full h-[60px] !text-teal-100 border !border-teal-100 hover:!text-REELTeal hover:!border-REELTeal hover:bg-gray-20"
                    type="secondary"
                    text="Cancel"
                    onClick={onCancel}
                />
                <Button
                    className="w-full h-[60px] !bg-teal-150 hover:!bg-REELTeal disabled:opacity-50 "
                    type="primary"
                    text={updateLabel}
                    onClick={onUpdate}
                    disabled={disabled}
                />
            </div>
        );
    };

    const renderBestPractices = () => {
        switch (activeTab) {
            case 'passwordReq':
                return (
                    <>
                        <button
                            onClick={() => setActiveTab(null)}
                            className={`${bestPractices} text-gray-800 mt-2`}
                        >
                            <ChevronLeftIcon className="w-5 h-5 mr-5 -ml-10" />
                            Password Requirements
                        </button>
                        <div className='text-[16px] overflow-y-auto'>
                            <p className='font-medium text-black'>
                                Password must meet each of the following requirements:
                            </p>
                            <div className="my-4 border-b border-b-gray-200" />
                            <ul className='pl-6 space-y-1 list-disc bg-gray-10'>
                                <li>Minimum of 10 characters</li>
                                <li>At least one uppercase letter</li>
                                <li>At least one lowercase letter</li>
                                <li>At least one numeral (0-9)</li>
                                <li>At least one symbol (@#^?_!{ }%)</li>
                            </ul>
                            <div className="my-4 border-b border-b-gray-200" />
                            <ul className='pl-6 space-y-1 list-disc'>
                                <li>Avoid using personal information such as your name, email, birthdate or address.</li>
                                <li>Passwords must be changed every 60 days; a reminder will be issued</li>
                                <li>Accounts inactive for 120 days or more will require re-registration upon return</li>
                                <li>For your security the system will automatically log out users after 15 minutes of inactivity</li>
                            </ul>
                        </div>
                    </>
                );

            case 'sharingLogin':
                return (
                    <>
                        <button
                            onClick={() => setActiveTab(null)}
                            className={`${bestPractices} text-gray-800 mt-2`}
                        >
                            <ChevronLeftIcon className="w-5 h-5 mr-5 -ml-10" />
                    Log-In Credentials
                        </button>
                        <div className='text-[16px] text-black'>
                            <p>
                        Sharing  login credentials is not permitted.
                            </p>
                            <div className="my-4 border-b border-b-gray-200" />
                            <ul className='pl-6 space-y-2 list-disc bg-gray-10'>
                                <li>Each user must use their own account to ensure data privacy and compliance.</li>
                                <li>Each member has its own access level via roles and permissions.</li>
                                <li>Sharing Log-In Credentials leaves your profile and REEL account vulnerable.</li>
                            </ul>
                        </div>
                    </>
                );

            case 'activityLog':
                return (
                    <>
                        <button
                            onClick={() => setActiveTab(null)}
                            className={`${bestPractices}  text-gray-800 mt-2`}
                        >
                            <ChevronLeftIcon className="w-5 h-5 mr-5 -ml-10" />
                            Application Activity Logs
                        </button>
                        <div className='text-[16px] text-black'>
                            <p>
                                Track and retain records of user activity and system events to ensure accountability, security, and regulatory compliance.
                            </p>
                            <div className="my-3 border-b border-b-gray-200" />
                            <p>REEL Outline keeps a history of all activities:</p>
                            <ul className='py-2 pl-6 space-y-1 list-disc bg-gray-10 mt-4 mb-4'>
                                <li>Application Activity Logs </li>
                                <li>Clinical Activity Logs</li>
                                <li>Administration Activity Logs </li>
                                <li>System Activity Logs</li>
                                <li>Audit Activity Logs</li>
                            </ul>
                            <div className="my-4 border-b border-b-gray-200" />
                            <p>
                                When users share profiles, it becomes difficult to identify who made specific changes within the application. This may result in your name being incorrectly associated with actions or updates you did not perform.
                            </p>
                        </div>
                    </>
                );

            case 'staffLeaves':
                return (
                    <>
                        <button
                            onClick={() => setActiveTab(null)}
                            className={`${bestPractices} text-gray-800 mt-2`}
                        >
                            <ChevronLeftIcon className="w-5 h-5 mr-5 -ml-10" />
                            When A Staff Member Leaves
                        </button>
                        <div className='text-[16px] text-black'>
                            <p>
                                Manage exits smoothly with clear offboarding processes.
                            </p>
                            <div className="my-4 border-b border-b-gray-200" />
                            <ul className='pl-6 space-y-3 list-disc bg-gray-10'>
                                <li>
                                    In the event that a staff member were to leave the Pharmacy organization, and if users were sharing profiles, you would need to reset the password for those REEL members
                                </li>
                                <li>
                                    You can pose the risk of that person accessing REEL through the shared profile after they have left the Organization.
                                </li>
                            </ul>
                        </div>
                    </>
                );

            default:
                return (
                    <>
                        <button
                            onClick={() => setActiveTab('passwordReq')}
                            className={`${bestPractices}`}
                        >
                            Password Requirements
                            <ChevronRightIcon className="w-5 h-5 ml-auto mr-4" />
                        </button>
                        <button
                            onClick={() => setActiveTab('sharingLogin')}
                            className={bestPractices}
                        >
                        Log-In Credentials
                            <ChevronRightIcon className="w-5 h-5 ml-auto mr-4" />
                        </button>

                        <button
                            onClick={() => setActiveTab('activityLog')}
                            className={bestPractices}
                        >
                            Application Activity Log
                            <ChevronRightIcon className="w-5 h-5 ml-auto mr-4" />
                        </button>

                        <button
                            onClick={() => setActiveTab('staffLeaves')}
                            className={bestPractices}
                        >
                            When A Staff Member Leaves
                            <ChevronRightIcon className="w-5 h-5 ml-auto mr-4" />
                        </button>
                    </>
                );
        }
    };

    return (

        <div className="flex flex-row w-full gap-2 flex-1 min-h-0 py-4">
            {/* Email */}
            <div className="mainCard flex flex-col flex-1 min-h-0 relative overflow-hidden">
                <div className="flex items-center w-full px-4 py-3 border-b border-gray-100 bg-gray-20 flex-shrink-0">
                    <h4 className="text-[22px] font-bold text-teal-600 pr-4">
                        Update Username
                    </h4>
                    <ReactSVG
                        src="/icons/information-dark.svg"
                        className="w-5"
                        data-tooltip-id="email"
                    />
                    <NewToolTip
                        id='email'
                        render={() => (
                            <div className="flex flex-col max-w-md p-2 bg-gray-20">
                                <p className="font-normal text-left text-teal-300 text-body-sm">
                                    Your username is the same
                                    <br /> as your email address.
                                </p>
                            </div>
                        )}
                    />
                </div>
                {/* Content - Scrollable with padding for absolute button */}
                <div className="flex-1 px-6 py-3.5 w-full min-h-0 overflow-y-auto pb-[92px]">
                    <div className="grid grid-cols-1">
                        <div>
                            <div>
                                <CustomInput
                                    label="Enter Current Username"
                                    name="current email"
                                    className='my-2 bg-white'
                                    type={'password'}
                                    value={user.primaryEmail}
                                    showOptionalLabel={false}
                                    passwordToggleLabel="Username"
                                    labelClassName="!text-gray-800"
                                />
                            </div>
                        </div>
                        <div className="pb-4 border-t border-gray-200 mt-4 "></div>
                        {(
                            <>
                                <CustomInput
                                    label="Create New Username"
                                    name="new email"
                                    className='my-2 bg-white'
                                    type={'password'}
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    hideHistory={true}
                                    showOptionalLabel={false}
                                    passwordToggleLabel="Username"
                                    labelClassName="!text-gray-800"
                                />
                                <CustomInput
                                    label="Confirm New Username"
                                    name="confirm new email"
                                    className='my-2 bg-white'
                                    type={'password'}
                                    value={confirmEmail}
                                    onChange={(e) => setConfirmEmail(e.target.value)}
                                    hideHistory={true}
                                    showOptionalLabel={false}
                                    passwordToggleLabel="Username"
                                    labelClassName="!text-gray-800"
                                />
                                {!isSelf && (
                                    <p className="text-sm text-red-600 py-1">You can only update your own email.</p>
                                )}

                            </>
                        )}
                    </div>
                </div>
                {/* Action Buttons - Absolutely positioned at bottom */}
                <div className="absolute bottom-0 left-0 right-0 ">
                    <ActionButtons
                        onCancel={() => { setNewEmail(""); setConfirmEmail(""); }}
                        onUpdate={handleSubmitEmail}
                        disabled={!canSubmitEmail || updateUsernameLoading}
                        updateLabel="Update & Submit"
                    />
                </div>
            </div>

            {/* Password */}
            <div className="mainCard flex flex-col flex-1 min-h-0 relative overflow-hidden">
                <div className="flex items-center justify-between w-full px-4 py-3 border-b border-gray-100 bg-gray-20 flex-shrink-0">
                    <h4 className="text-[22px] font-bold text-teal-600">
                        Update Password
                    </h4>
                </div>
                {/* Content - Scrollable with padding for absolute button */}
                <div className="flex-1 px-6 py-3.5 w-full min-h-0 overflow-y-auto pb-[92px]">
                    <div className="grid grid-cols-1">
                        <div>
                            <CustomInput
                                label="Enter Current Password"
                                name="Current Password"
                                className='my-2 bg-white'
                                type={'password'}
                                showPasswordTooltip={false}
                                value={currentPassword}
                                onChange={(e) => {
                                    setCurrentPassword(e.target.value);
                                    // Reset validation state when user types
                                    if (currentPasswordValid !== null) {
                                        setCurrentPasswordValid(null);
                                        setCurrentPasswordError("");
                                    }
                                }}
                                hideHistory={true}
                                showOptionalLabel={false}
                                labelClassName="!text-gray-800"
                            />
                            {/* Auto-validation feedback */}
                            {currentPassword && currentPasswordValid === true && (
                                <div className="flex items-center gap-1 mt-1">
                                    <span className="text-teal-600 text-sm flex items-center gap-1">
                                        ✓ Password verified
                                    </span>
                                </div>
                            )}
                            {currentPassword && currentPasswordValid === false && (
                                <div className="flex items-center gap-1 mt-1">
                                    <span className="text-red-600 text-sm flex items-center gap-1">
                                        ✗ Current password is incorrect
                                    </span>
                                </div>
                            )}
                            {validateCurrentPasswordLoading && currentPassword && (
                                <div className="flex items-center gap-1 mt-1">
                                    <span className="text-blue-600 text-sm flex items-center gap-1">
                                        🔄 Validating...
                                    </span>
                                </div>
                            )}
                            {currentPasswordError && (
                                <p className="text-sm text-red-600 py-1">{currentPasswordError}</p>
                            )}
                        </div>
                        <div className="pb-4 border-t border-gray-200 mt-4 "></div>
                        {(
                            <>
                                <CustomInput
                                    label="Create New Password"
                                    name="newPassword"
                                    className='my-2 bg-white'
                                    type={'password'}
                                    showPasswordTooltip={false}
                                    value={password}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setPassword(val);
                                        setPasswordValidity(validatePassword(val, user));
                                    }}
                                    hideHistory={true}
                                    showOptionalLabel={false}
                                    labelClassName="!text-gray-800"
                                />

                                <CustomInput
                                    label="Confirm New Password"
                                    name="confirm new password"
                                    className='my-2 bg-white'
                                    type={'text'}
                                    showPasswordTooltip={false}
                                    value={passwordAgain}
                                    onChange={(e) => setPasswordAgain(e.target.value)}
                                    hideHistory={true}
                                    showOptionalLabel={false}
                                    labelClassName="!text-gray-800"
                                />
                                {!isSelf && (
                                    <p className="text-sm text-red-600 py-1">You can only update your own password.</p>
                                )}

                                {password && (
                                    <div className="border border-gray-200 rounded-md p-3 bg-teal-10 mt-2 mb-2 text-sm text-teal-300">
                                        <div className="font-medium text-teal-300 text-[18px] mb-2">Your password must contain:</div>
                                        <ul className="space-y-2">
                                            <li className="flex items-center gap-3 text-[14px] font-normal leading-relaxed">
                                                {renderValidationDot(passwordValidity.isLongEnough)}
                                                <span>Minimum of 10 characters</span>
                                            </li>
                                            <li className="flex items-center gap-3 text-[14px] font-normal leading-relaxed">
                                                {renderValidationDot(passwordValidity.hasUpperCase)}
                                                <span>At least one uppercase letter</span>
                                            </li>
                                            <li className="flex items-center gap-3 text-[14px] font-normal leading-relaxed">
                                                {renderValidationDot(passwordValidity.hasLowerCase)}
                                                <span>At least one lowercase letter</span>
                                            </li>
                                            <li className="flex items-center gap-3 text-[14px] font-normal leading-relaxed">
                                                {renderValidationDot(passwordValidity.hasNumber)}
                                                <span>At least one numeral (0–9)</span>
                                            </li>
                                            <li className="flex items-center gap-3 text-[14px] font-normal leading-relaxed">
                                                {renderValidationDot(passwordValidity.hasSymbol)}
                                                <span>At least one symbol (@#^?_!{ }%)</span>
                                            </li>
                                            <li className="flex items-start gap-3 text-[14px] font-normal leading-relaxed">
                                                {renderValidationDot(passwordValidity.noPersonalInfo, 'mt-0.5')}
                                                <span className="pt-0.5">Avoid using personal information such as your name, email, birthdate or address.</span>
                                            </li>
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                {/* Action Buttons - Absolutely positioned at bottom */}
                <div className="absolute bottom-0 left-0 right-0">
                    <ActionButtons
                        onCancel={() => { 
                            setCurrentPassword(""); 
                            setPassword(""); 
                            setPasswordAgain(""); 
                            setCurrentPasswordValid(null);
                            setCurrentPasswordError("");
                        }}
                        onUpdate={async () => {
                            if (changeMyProfilePasswordLoading) return;

                            if (currentPasswordValid !== true) {
                                setCurrentPasswordError("Please validate your current password first.");
                                return;
                            }

                            if (!canAttemptPasswordUpdate) return;

                            const userId = profileUserId;
                            if (!userId) return;

                            await dispatch(changeMyProfilePassword(userId, currentPassword, password));
                        }}
                        disabled={!canAttemptPasswordUpdate || changeMyProfilePasswordLoading}
                        updateLabel="Update & Submit"
                    />
                </div>
            </div>

            {/* Best Practices  */}
            <div className="mainCard !bg-white flex flex-col flex-1 min-h-0 relative overflow-hidden">
                <div className="flex items-center justify-between w-full px-4 py-3 border-b border-gray-100 bg-gray-20 flex-shrink-0">
                    <h4 className="text-[22px] font-bold text-teal-600">
                        Best Practices
                    </h4>
                </div>
                {/* Content */}
                <div className="flex-1 px-6 py-3.5 w-full overflow-y-auto min-h-0">
                    <div className="grid grid-cols-1">
                        {renderBestPractices()}
                    </div>
                </div>
            </div>

            {/* Credential Updated Popup - Reusable for both password and username */}
            {((showPasswordChangedPopup && !hasPasswordHistoryError) || showUsernameUpdatedPopup) && (
                <ConfirmationModalNew
                    open={(showPasswordChangedPopup && !hasPasswordHistoryError) || showUsernameUpdatedPopup}
                    title={showPasswordChangedPopup ? "Password Updated" : "Username Updated"}
                    titleClassName={showUsernameUpdatedPopup ? "text-[24px]" : undefined}
                    className='!p-0 !w-[630px] !min-w-[630px] !max-w-[630px]'
                    content={() => {
                        const isPassword = showPasswordChangedPopup;
                        const reason = isPassword ? 'password-updated' : 'username-updated';
                        const credentialType = isPassword ? 'password' : 'username';
                        
                        return (
                            <div className="text-left">
                                <p className="text-base font-medium text-gray-800 mb-1">
                                    For your security, your current session has ended.
                                </p>
                                <p className="text-base font-medium text-gray-800 mb-3">
                                    Please{isPassword ? ',' : ''} sign in again with your new {credentialType} to continue.
                                </p>
                                <p className="text-sm text-gray-500 mb-8">
                                    Your identity will be verified using multi-factor authentication at your next login.
                                </p>

                                <div className="flex justify-center">
                                    <Button
                                        className="w-[282px]"
                                        type="primary"
                                        text="Go to Sign-In"
                                        onClick={() => {
                                            dispatch(logout());
                                            setShowPasswordChangedPopup(false);
                                            setShowUsernameUpdatedPopup(false);
                                            navigate(`/login?reason=${reason}`);
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    }}
                    onCross={() => {
                        const isPassword = showPasswordChangedPopup;
                        const reason = isPassword ? 'password-updated' : 'username-updated';
                        dispatch(logout());
                        setShowPasswordChangedPopup(false);
                        setShowUsernameUpdatedPopup(false);
                        navigate(`/login?reason=${reason}`);
                    }}
                    type="primary"
                    hideActions={true}
                    hideCloseIcon={false}
                />
            )}

            {/* Password History Error Popup - shown when password history error occurs */}
            {showPasswordChangedPopup && hasPasswordHistoryError && (
                <PasswordHistoryErrorModal
                    open={showPasswordChangedPopup}
                    onClose={() => {
                        setShowPasswordChangedPopup(false);
                        setHasPasswordHistoryError(false);
                        dispatch({ type: "CLEAR_CHANGE_MYPROFILE_PASSWORD_ERROR" });
                    }}
                    clearErrorActionType="CLEAR_CHANGE_MYPROFILE_PASSWORD_ERROR"
                />
            )}
        </div>
    )
}
export default EmailPassword;